import React, { useEffect, useRef, useState } from 'react';
import { JoyInteractableMap } from '@shared/components/JoyInteractableMap';
import { EventVenueState } from '../EventVenueDialog/useEventVenueDialog';
import { DirectionsRenderer } from '@react-google-maps/api';
import { withDirectionsResult } from '@shared/components/DirectionsMap/withDirectionsResult';
import { useGoogleInteractableMap } from '@shared/components/JoyInteractableMap/JoyInteractableMap.utils';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { useJoyInteractableMapContext } from '@shared/components/JoyInteractableMap/JoyInteractableMap.provider';
import { AccommodationPropertyWithCalculatedPrice } from '@shared/hooks/useSearchExtendedAccommodationProperties';
import { useWaitForTimeout } from '@shared/utils/hooks/useWaitForTimeout';
import { MapVenueMarker } from './MapVenueMarker';
import { HotelCardWithIntent } from '../HotelCardWithIntent';
import { Box } from '@withjoy/joykit';

const DEBOUNCE_TIMEOUT_MS = 1000;

interface MapProps {
  eventPhoto?: string;
  eventVenueState: EventVenueState;
  hideDirection?: boolean;
  originList: AccommodationPropertyWithCalculatedPrice[];
  destination: google.maps.LatLngLiteral;
  isLoading: boolean;
  children?: React.ReactNode;
}

const MapRoot = (props: MapProps & { directions?: google.maps.DirectionsResult | null }) => {
  const { eventPhoto, destination, eventVenueState, hideDirection, directions, originList, isLoading, children } = props;
  const waitForTimeout = useWaitForTimeout();

  const mapContainerRef = useRef<HTMLDivElement>(null);
  const updatedEventVenueState = useRef<EventVenueState>(eventVenueState);
  const [activePinId, setActivePinId] = useState<string>();
  const [hasUpdatedMarkers, setHasUpdatedMarkers] = useState(false);
  const markers: google.maps.LatLngLiteral[] = originList.map(originLocation => ({ lat: originLocation.lat, lng: originLocation.lng }));

  const { map } = useJoyInteractableMapContext();
  const { getCalculatedCenterAndZoom } = useGoogleInteractableMap();

  const showAllMarkersOnMap = useEventCallback((originList: google.maps.LatLngLiteral[]) => {
    waitForTimeout(DEBOUNCE_TIMEOUT_MS, () => {
      if (originList.length && map) {
        const centerAndZoom = getCalculatedCenterAndZoom ? getCalculatedCenterAndZoom(originList) : undefined;
        if (centerAndZoom) {
          map.fitBounds(centerAndZoom.bounds);
          setHasUpdatedMarkers(true);
        }
      }
    });
  });

  const handleOnClickHotelPin = useEventCallback((id: string, location: google.maps.LatLngLiteral, show: boolean) => {
    if (show) {
      map?.panTo(location);
      map?.panBy(0, -100);
      setActivePinId(id);
    } else {
      map?.panTo(destination);
      setActivePinId(undefined);
    }
  });

  const resetActivePins = useEventCallback((e: MouseEvent) => {
    if (mapContainerRef.current?.contains(e.target as Node)) {
      return;
    }
    e.stopPropagation();
    if (activePinId) {
      setActivePinId(undefined);
      map?.panTo(destination);
    }
  });

  useEffect(() => {
    if (markers.length && (!hasUpdatedMarkers || updatedEventVenueState.current !== eventVenueState)) {
      updatedEventVenueState.current = eventVenueState;
      markers.push(destination);
      showAllMarkersOnMap(markers);
      // Clear the active pin selection when the event venue state changes,
      setActivePinId(undefined);
    }
  }, [destination, markers, hasUpdatedMarkers, showAllMarkersOnMap, eventVenueState]);

  return (
    <Box
      ref={mapContainerRef}
      position="relative"
      width="100%"
      height="100%"
      flex={1}
    >
      <JoyInteractableMap
        center={destination}
        autoCenterAndZoom
        radiusMiles={5}
        gestureHandling="cooperative"
        onClick={resetActivePins}
      >
        {children}
        {!hideDirection && directions && (
          <DirectionsRenderer
            directions={directions}
            options={{
              suppressMarkers: true,
              polylineOptions: {
                strokeColor: '#1097FF',
                strokeWeight: 5,
              },
            }}
          />
        )}
        {originList.map((originLocation, idx) => (
          <HotelCardWithIntent
            key={`${originLocation.lat}-${originLocation.lng}-${idx}`}
            property={originLocation}
            location={originLocation}
            index={idx}
            activePinId={activePinId}
            isLoading={isLoading}
            eventVenueState={eventVenueState}
            onClickHotelPin={handleOnClickHotelPin}
          />
        ))}
        <MapVenueMarker
          eventPhoto={eventPhoto}
          location={destination}
          pinSize={eventVenueState === EventVenueState.create || eventVenueState === EventVenueState.update ? 'md' : 'sm'}
        />
      </JoyInteractableMap>
    </Box>
  );
};

const Map = withDirectionsResult(MapRoot);

Map.displayName = 'VenueMap';

export { Map };
